import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  TextareaAutosize,
  IconButton,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  TableHead,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import * as pdfjsLib from "pdfjs-dist";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { searchPlugin } from '@react-pdf-viewer/search';
import { thumbnailPlugin } from '@react-pdf-viewer/thumbnail';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';
import '@react-pdf-viewer/thumbnail/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '../../css/OutPutDirectory.css';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { pdfDetails, pdfDownload } from '../../redux/slices/PdfSlice';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import Snackbar from '@mui/material/Snackbar';
import { ArrowBack } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const ActivityDetailsScreen = () => {
  const PdfDataSlice = useSelector((state) => state.PdfDataSlice);
  const [editIndex, setEditIndex] = useState(null);
  const [editedValue, setEditedValue] = useState("");
  const [editingCell, setEditingCell] = useState(null);
  const [editedValues, setEditedValues] = useState({});

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [tabIndex, setTabIndex] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [selectedPage, setSelectedPage] = useState(0);
  const theme = useTheme();
  const { state } = useLocation();
  const { mode, row } = state || {};
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const searchPluginInstance = searchPlugin();
  const thumbnailPluginInstance = thumbnailPlugin();
  const { Thumbnails } = thumbnailPluginInstance;
  const zoomPluginInstance = zoomPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;
  const isEditMode = mode === 'edit';

  useEffect(() => {
    dispatch(pdfDetails(state?.row?.id))
    dispatch(pdfDownload(state?.row?.id))
  }, [state])

  const pdfUrl = PdfDataSlice?.pdfFile?.base64_data
    ? `data:application/pdf;base64,${PdfDataSlice.pdfFile.base64_data}`
    : null;

  const pdfData = PdfDataSlice?.pdfData?.data?.data || [];
  const selectedPdfData = pdfData[selectedPage] || {};

  const isPdfFileLoading = PdfDataSlice?.pdfFileLoading;
  const isPdfDataLoading = PdfDataSlice?.pdfDataLoading;

  const createAndDownloadFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to flatten objects and arrays for CSV export
  const flattenObject = (obj, prefix = "") => {
    let result = {};
    Object.entries(obj).forEach(([key, value]) => {
      const newKey = prefix ? `${prefix} > ${key}` : key;
      if (typeof value === "object" && !Array.isArray(value)) {
        Object.assign(result, flattenObject(value, newKey));
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          Object.assign(result, flattenObject(item, `${newKey} [${index}]`));
        });
      } else {
        result[newKey] = value;
      }
    });
    return result;
  };

  // CSV Export Function
  const exportToCSV = () => {
    const updatedFileName = 'Report';
    const flattenedData = pdfData.map(item => flattenObject(item));
    const headers = Array.from(new Set(flattenedData.flatMap(obj => Object.keys(obj))));

    let csvContent = headers.join(",") + "\n";
    csvContent += flattenedData
      .map(item => headers.map(header => `"${item[header] || ''}"`).join(","))
      .join("\n");

    createAndDownloadFile(csvContent, `${updatedFileName}.csv`, 'text/csv;charset=utf-8;');
  };

  // XML Export Function
  const exportToXML = () => {
    const updatedFileName = 'Report';

    const convertToXML = (obj, indent = "  ") => {
      let xml = "";
      Object.entries(obj).forEach(([key, value]) => {
        if (typeof value === "object" && !Array.isArray(value)) {
          xml += `${indent}<${key}>\n${convertToXML(value, indent + "  ")}${indent}</${key}>\n`;
        } else if (Array.isArray(value)) {
          value.forEach(item => {
            xml += `${indent}<${key}>\n${convertToXML(item, indent + "  ")}${indent}</${key}>\n`;
          });
        } else {
          xml += `${indent}<${key}>${value}</${key}>\n`;
        }
      });
      return xml;
    };

    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<root>\n';
    pdfData.forEach(item => {
      xmlContent += `  <item>\n${convertToXML(item, "    ")}  </item>\n`;
    });
    xmlContent += '</root>';
    createAndDownloadFile(xmlContent, `${updatedFileName}.xml`, 'application/xml;charset=utf-8;');
  };

  // JSON Export Function
  const exportToJSON = () => {
    const jsonString = JSON.stringify(pdfData, null, 2);
    const updatedFileName = 'Report';
    createAndDownloadFile(jsonString, `${updatedFileName}.json`, 'application/json;charset=utf-8;');
  };

  const renderThumbnailItem = ({
    pageIndex,
    renderPageThumbnail,
    onJumpToPage,
  }) => (
    <div
      key={pageIndex}
      onClick={() => {
        setSelectedPage(pageIndex);
        onJumpToPage(pageIndex);
        setEditIndex(null);
        setEditedValue('');
      }}
      style={{
        cursor: "pointer",
        padding: "5px",
        border: selectedPage === pageIndex ? "2px solid blue" : "none",
      }}
    >
      {renderPageThumbnail}
    </div>
  );

  const handleCardClick = (subIndex, field, value) => {
    const key = `${subIndex}-${field}`;
    const stringValue = String(value).trim();
    const words = stringValue.replace(/\n/g, ' ').split(' ').filter(Boolean);
    searchPluginInstance.clearHighlights();

    words.forEach((word) => {
      searchPluginInstance.highlight({
        keyword: word,
        matchCase: true,
        wholeWords: false,
      });
    });

    setEditingCell(key);
    setEditedValues((prev) => ({
      ...prev,
      [key]: typeof value === "object" ? JSON.stringify(value, null, 2) : value,
    }));
  };

  const handleClearHighlights = () => {
    searchPluginInstance.clearHighlights();
  };

  const handleSaveEdit = (index) => {
    setEditIndex(null);
    setEditedValue('');
    setShowSnackbar(true);
    handleClearHighlights();
  };

  const handleCopyToClipboard = (value) => {
    navigator.clipboard.writeText(value)
      .then(() => {
        console.log('Copied to clipboard:', value);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
      });
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSBClose = () => {
    setShowSnackbar(false);
  };

  const action = (
    <React.Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleSBClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setEditIndex(null);
    setEditedValue('');
  };

  const handleFocus = (event) => {
    event.target.scrollIntoView({ block: 'nearest', inline: 'start' });
  };

  const getRowCount = (value) => {
    const strValue = String(value);
    const lineBreaks = (strValue.match(/\n/g) || []).length;
    return Math.max(1, lineBreaks + 1);
  };

  return (
    <div>
      <div style={{ marginLeft: 20, display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }} className="nav-title">
        <div style={{ flexDirection: 'row', display: 'flex' }}>
          <button
            onClick={() => navigate("/NewAgentViewerScreen")}
            className="back-button"
          >
            <ArrowBack />
          </button>
          <h3>{"Documents"}</h3>
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleClick}
          sx={{ width: "113px", fontSize: "12px", fontFamily: "'Poppins', sans-serif", marginRight: 10 }}
        >
          Download
        </Button>

      </div>
      <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        {/* Thumbnails Section */}
        <Box
          sx={{
            width: "15%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {pdfUrl ? (
            <Thumbnails renderThumbnailItem={renderThumbnailItem} />
          ) : null}
        </Box>
        {/* PDF Section */}
        <Box
          sx={{
            width: "55%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {isPdfFileLoading ? (
            <Box
              sx={{
                // position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                zIndex: 10,
              }}
            >
              <CircularProgress color="inherit" />
            </Box>
          ) : pdfUrl ? (
            <div
              className="rpv-core__viewer"
              style={{
                border: '1px solid rgba(0, 0, 0, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
              }}
            >
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: '#eeeeee',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '4px',
                }}
              >
                <ZoomOutButton />
                <ZoomPopover />
                <ZoomInButton />
              </div>
              <div
                style={{
                  flex: 1,
                  overflow: 'hidden',
                }}
              >
                <Worker workerUrl={pdfjsLib.GlobalWorkerOptions.workerSrc}>
                  <Viewer fileUrl={pdfUrl}
                    plugins={[
                      searchPluginInstance,
                      thumbnailPluginInstance,
                      zoomPluginInstance
                    ]}
                  />
                </Worker>
              </div>
            </div>
          ) : (
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                fontSize: "1.2rem",
                color: "gray",
              }}
            >
              PDF Not Available
            </Typography>
          )}

        </Box>
        {/* Right Side Card Content */}
        <Box
          sx={{
            width: "40%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "hidden",
            padding: "10px",
            position: "relative",
          }}
        >
          {isPdfDataLoading ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                zIndex: 10,
              }}
            >
              <CircularProgress color="inherit" />
            </Box>
          ) : (
            <>
              {/* Download Button */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", position: "relative" }}>

                <Menu
                  id="download-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{
                    "aria-labelledby": "download-button",
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      exportToCSV();
                      handleClose();
                    }}
                    sx={{ width: "113px", fontSize: "10px", fontFamily: "'Poppins', sans-serif" }}
                  >
                    <InsertDriveFileIcon
                      style={{ marginRight: "8px", color: theme.palette.primary.main }}
                    />
                    CSV
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      exportToXML();
                      handleClose();
                    }}
                    sx={{ width: "113px", fontSize: "10px", fontFamily: "'Poppins', sans-serif" }}
                  >
                    <TextSnippetIcon
                      style={{ marginRight: "8px", color: theme.palette.primary.main }}
                    />
                    XML
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      exportToJSON();
                      handleClose();
                    }}
                    sx={{ width: "113px", fontSize: "10px", fontFamily: "'Poppins', sans-serif" }}
                  >
                    <DescriptionIcon
                      style={{ marginRight: "8px", color: theme.palette.primary.main }}
                    />
                    JSON
                  </MenuItem>
                </Menu>
                {/* Snackbar */}
                <Snackbar
                  open={showSnackbar}
                  autoHideDuration={3000}
                  onClose={handleSBClose}
                  message="Data saved"
                  action={action}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    left: 'calc(100% + 10px)',
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "1rem",
                  boxSizing: "border-box",
                  height: '100%',
                }}
              >
                <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  sx={{
                    "& .MuiTabs-indicator": {
                      height: '2px',
                      borderBottom: '2px solid blue',
                    },
                    mb: 2
                  }}
                >
                  <Tab
                    label={
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: "1rem",
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "bold",
                          position: 'relative',
                          left: '-15px',
                          textTransform: 'none'
                        }}
                      >
                        Fields
                      </Typography>
                    }
                  />
                  <Tab
                    label={
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: "1rem",
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "bold",
                          textTransform: 'none'
                        }}
                      >
                        Tables
                      </Typography>
                    }
                  />
                </Tabs>
                {/* Fields Section */}
                {tabIndex === 0 && (
                  <Typography>
                    <Table sx={{ minWidth: 300, marginBottom: "1rem" }}>
                      <TableBody>
                        {selectedPdfData &&
                          Object.entries(selectedPdfData)
                            .filter(([key]) => key !== "Items" && key !== "Line Items")
                            .map(([field, value], subIndex) => {
                              const cellKey = `${subIndex}-${field}`;
                              const isEditing = editingCell === cellKey;
                              const displayValue =
                                editedValues[cellKey] ??
                                (typeof value === "object" ? JSON.stringify(value, null, 2) : value);

                              return (
                                <TableRow
                                  key={cellKey}
                                  onClick={() => {
                                    if (isEditMode) handleCardClick(subIndex, field, value);
                                  }}

                                  sx={{
                                    cursor: "pointer",
                                    '&:hover': {
                                      backgroundColor: "lightgray",
                                    },
                                    transition: "background-color 0.3s ease",
                                  }}
                                >
                                  <TableCell sx={{ padding: "8px 12px", border: "none" }}>
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        fontFamily: "'Poppins', sans-serif",
                                        fontWeight: 'bold'
                                      }}
                                      color="text.primary"
                                    >
                                      {field?.replace(/^p_/, "").replace(/([a-z])([A-Z])/g, "$1 $2")}
                                    </Typography>
                                  </TableCell>

                                  <TableCell sx={{ padding: "8px 12px", border: "none" }}>
                                    {isEditMode && isEditing ? (
                                      <TextareaAutosize
                                        onFocus={handleFocus}
                                        minRows={getRowCount(value)}
                                        maxRows={6}
                                        value={displayValue}
                                        onChange={(e) =>
                                          setEditedValues((prev) => ({
                                            ...prev,
                                            [cellKey]: e.target.value,
                                          }))
                                        }
                                        style={{
                                          padding: "8px",
                                          border: "1px solid #ccc",
                                          borderRadius: "4px",
                                          fontSize: "14px",
                                          resize: "vertical",
                                          width: "100%",
                                          fontFamily: "'Poppins', sans-serif",
                                        }}
                                      />
                                    ) : (
                                      <Typography
                                        variant="h6"
                                        component="div"
                                        sx={{
                                          fontSize: "12px",
                                          fontFamily: "'Poppins', sans-serif",
                                        }}
                                      >
                                        {displayValue}
                                      </Typography>
                                    )}
                                  </TableCell>

                                  {isEditing && (
                                    <TableCell
                                      sx={{
                                        padding: "8px 12px",
                                        border: "none",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <IconButton
                                          sx={{
                                            backgroundColor: '#1E3899',
                                            borderRadius: '50%',
                                            transition: 'background-color 0.3s',
                                            '&:hover': { backgroundColor: '#2AD899' },
                                          }}
                                          onClick={() => handleCopyToClipboard(displayValue)}
                                        >
                                          <Tooltip title="Copy To Clipboard" arrow>
                                            <ContentCopyIcon style={{ color: 'white' }} />
                                          </Tooltip>
                                        </IconButton>
                                      </Stack>
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            })}
                      </TableBody>
                    </Table>
                  </Typography>
                )}

                {tabIndex === 1 && (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 300, marginBottom: "1rem" }} size="small">
                      <TableBody>
                        {(() => {
                          const tableData = Array.isArray(selectedPdfData?.Items)
                            ? selectedPdfData.Items
                            : Array.isArray(selectedPdfData?.["Line Items"])
                              ? selectedPdfData["Line Items"]
                              : [];

                          return tableData.map((subItem, subIndex) => (
                            <Accordion key={subIndex} defaultExpanded>
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                  backgroundColor: theme.palette.primary.main,
                                  color: '#fff',
                                  fontFamily: "'Poppins', sans-serif",
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                }}
                              >
                                Item #{subIndex + 1}
                              </AccordionSummary>
                              <AccordionDetails>
                                <Table size="small" sx={{ minWidth: 300 }}>
                                  <TableBody>
                                    {Object.entries(subItem).map(([field, value], fieldIndex) => {
                                      const cellKey = `${subIndex}-${field}`;
                                      const isEditing = editingCell === cellKey;
                                      const displayValue = editedValues[cellKey] ?? (typeof value === "object" ? JSON.stringify(value, null, 2) : value);
                                      return (
                                        <TableRow
                                          key={cellKey}
                                          onClick={() => handleCardClick(subIndex, field, value)}
                                          sx={{
                                            cursor: "pointer",
                                            '&:hover': { backgroundColor: "#e0f7fa" },
                                            backgroundColor: (subIndex * Object.keys(subItem).length + fieldIndex) % 2 === 0 ? "#f9f9f9" : "#fff",
                                            transition: "background-color 0.3s ease",
                                          }}
                                        >
                                          <TableCell
                                            align="left"
                                            sx={{
                                              padding: "10px 12px",
                                              fontSize: "12px",
                                              fontFamily: "'Poppins', sans-serif",
                                              fontWeight: "bold",
                                              width: '35%',
                                            }}
                                          >
                                            {field?.replace(/^p_/, "").replace(/([a-z])([A-Z])/g, "$1 $2")}
                                          </TableCell>

                                          <TableCell
                                            align="left"
                                            sx={{
                                              padding: "10px 12px",
                                              fontSize: "12px",
                                              fontFamily: "'Poppins', sans-serif",
                                            }}
                                          >
                                            {isEditMode && isEditing ? (
                                              <TextareaAutosize
                                                onFocus={handleFocus}
                                                minRows={getRowCount(value)}
                                                maxRows={6}
                                                value={displayValue}
                                                onChange={(e) =>
                                                  setEditedValues((prev) => ({
                                                    ...prev,
                                                    [cellKey]: e.target.value,
                                                  }))
                                                }
                                                style={{
                                                  padding: "8px",
                                                  border: "1px solid #ccc",
                                                  borderRadius: "4px",
                                                  fontSize: "14px",
                                                  resize: "vertical",
                                                  width: "100%",
                                                  fontFamily: "'Poppins', sans-serif",
                                                }}
                                              />
                                            ) : (
                                              <Typography
                                                variant="h6"
                                                component="div"
                                                sx={{
                                                  fontSize: "12px",
                                                  fontFamily: "'Poppins', sans-serif",
                                                  wordBreak: 'break-word',
                                                }}
                                              >
                                                {displayValue}
                                              </Typography>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </AccordionDetails>
                            </Accordion>
                          )
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default ActivityDetailsScreen;
